import type { ColorScheme } from '../theme';

export type MapboxStyleURLs = {
  dark: string;
  light: string;
};

export type MapColorMode = 'default' | 'light' | 'dark' | 'inverted';

export type MapboxStyleResponse = {
  styleURL?: string;
  styleURLs?: Partial<MapboxStyleURLs>;
};

const MAP_COLOR_MODES = new Set<MapColorMode>(['default', 'light', 'dark', 'inverted']);
const DEFAULT_MAP_COLOR_MODE: MapColorMode = 'default';

let currentMapColorMode: MapColorMode = DEFAULT_MAP_COLOR_MODE;
let hasLoadedStoredMapColorMode = false;
const mapColorModeListeners = new Set<(mode: MapColorMode) => void>();

const supportsLocalStorage = () => typeof window !== 'undefined' && !!window.localStorage;

export const normalizeMapColorMode = (value: unknown): MapColorMode =>
  typeof value === 'string' && MAP_COLOR_MODES.has(value.trim().toLowerCase() as MapColorMode)
    ? (value.trim().toLowerCase() as MapColorMode)
    : DEFAULT_MAP_COLOR_MODE;

const readStoredMapColorMode = (): MapColorMode => {
  if (!supportsLocalStorage()) {
    return DEFAULT_MAP_COLOR_MODE;
  }
  try {
    const raw = window.localStorage.getItem('appConfig');
    if (!raw) {
      return DEFAULT_MAP_COLOR_MODE;
    }
    const parsed = JSON.parse(raw) as { map_color_mode?: unknown };
    return normalizeMapColorMode(parsed.map_color_mode);
  } catch {
    return DEFAULT_MAP_COLOR_MODE;
  }
};

export const getCurrentMapColorMode = (): MapColorMode => {
  if (!hasLoadedStoredMapColorMode) {
    currentMapColorMode = readStoredMapColorMode();
    hasLoadedStoredMapColorMode = true;
  }
  return currentMapColorMode;
};

export const applyMapColorModePreference = (mode: MapColorMode) => {
  const normalizedMode = normalizeMapColorMode(mode);
  hasLoadedStoredMapColorMode = true;
  currentMapColorMode = normalizedMode;
  for (const listener of mapColorModeListeners) {
    listener(normalizedMode);
  }
  return normalizedMode;
};

export const subscribeToMapColorMode = (listener: (mode: MapColorMode) => void) => {
  mapColorModeListeners.add(listener);
  return () => {
    mapColorModeListeners.delete(listener);
  };
};

export const resolveMapColorScheme = (
  colorScheme: ColorScheme,
  mapColorMode: MapColorMode = getCurrentMapColorMode(),
): ColorScheme => {
  switch (mapColorMode) {
    case 'light': {
      return 'light';
    }
    case 'dark': {
      return 'dark';
    }
    case 'inverted': {
      return colorScheme === 'dark' ? 'light' : 'dark';
    }
    default: {
      return colorScheme;
    }
  }
};

export const normalizeMapboxStyleURLs = (response: MapboxStyleResponse): MapboxStyleURLs => {
  const fallback = response.styleURL;
  const dark = response.styleURLs?.dark ?? fallback;
  const light = response.styleURLs?.light ?? fallback;

  if (!dark || !light) {
    throw new Error('Invalid Mapbox style URLs');
  }

  return { dark, light };
};

export const selectMapboxStyleURL = (
  styleURLs: MapboxStyleURLs,
  colorScheme: ColorScheme,
  mapColorMode: MapColorMode = getCurrentMapColorMode(),
): string => styleURLs[resolveMapColorScheme(colorScheme, mapColorMode)] ?? styleURLs.dark;
